/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/* eslint-disable complexity */
/* eslint-disable prefer-arrow/prefer-arrow-functions */
/* eslint-disable max-lines-per-function */
/**
 * 菜单配置
 */

import { type I18nService, type LocalStorageService } from '@dm/core';
import { type MenuItem } from '@dm/core/types';
import { STORAGE_KEY } from 'app/common/constants';
import { LicenseKey } from 'app/common/constants/license-key';
import { type AuthService } from 'app/common/services';

import { FeatureConfig } from './feature';
import { ProductConfig, ShardSwitch, SWITCH } from './switch';

// 顶部菜单配置
export function rootMenus(i18n: I18nService, authService: AuthService, localStorageService: LocalStorageService): MenuItem[] {
    // dorado服务模块菜单
    const generalServiceMenus = [
        // 微存储屏蔽多租户
        ...(SWITCH.IS_VSTORE_SYSTEM || !FeatureConfig.IsSupportVStoreService ? [] : [
            {
                title: i18n.get('service.vstore_term_vstoreService_title'),
                children: [
                    {
                        name: i18n.get('service.vstore_term_vstores_title'),
                        url: '/v-store-service/v-store',
                        show: [
                            {
                                permission: ['@vstore_R'],
                            },
                        ],
                    },
                ],
            },
        ]),
        {
            title: i18n.get('common.blockService_title'),
            children: [
                {
                    name: i18n.get('common.lunGroup_title'),
                    url: `/block/${FeatureConfig.IsReplaceStrLunWithNamespace ? 'namespace-group' : 'lun-group'}`,
                    show: [
                        {
                            permission: [['lun_R', 'recycle_bin_R', 'lun_group_R']],
                            license: FeatureConfig.IsReplaceStrLunWithNamespace ? [] : [LicenseKey.SmartThin],
                        },
                    ],
                },
                {
                    name: i18n.get('common.hostGroups_title'),
                    url: '/block/hosts',
                    show: [
                        {
                            permission: ['host_R'],
                            license: [LicenseKey.SmartThin],
                        },
                        {
                            permission: ['host_group_R'],
                            license: [LicenseKey.SmartThin],
                        },
                        {
                            permission: ['initiator_R'],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                },
                {
                    name: i18n.get('common.portGroups_title'),
                    url: '/block/port-group',
                    show: [
                        {
                            permission: ['port_group_R'],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                },
                {
                    name: i18n.get('storage.vvol_task_vvol_tittle'),
                    url: '/block/vvol',
                    show: [
                        {
                            permission: [['lun_R', 'protect_group_R']],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                    hide: !FeatureConfig.IsSupportVVOL,
                },
                {
                    name: i18n.get('storage.mappingView_task_title'),
                    url: '/block/mapping-views',
                    show: [
                        {
                            permission: ['mapping_view_R'],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                },
            ],
        },
        ...(SWITCH.SUPPORT_NAS ? [
            {
                title: i18n.get('common.fileService_title'),
                children: [
                    {
                        name: i18n.get('common.fileSystem_title'),
                        url: '/file/file-system',
                        show: [
                            {
                                permission: ['file_system_R'],
                            },
                        ],
                    },
                    {
                        name: i18n.get('common.dtree_lab'),
                        url: '/file/dtree',
                        show: [
                            {
                                permission: ['dtree_R'],
                            },
                        ],
                    },
                    {
                        name: i18n.get('common.share_title'),
                        url: '/file/share',
                        show: [
                            {
                                permission: ['share_R'],
                            },
                        ],
                    },
                    {
                        name: i18n.get('service.DataTurboClient_title'),
                        url: '/file/data-turbo-client',
                        show: [
                            {
                                permission: ['data_turbo_R'],
                            },
                        ],
                        hide: SWITCH.IS_VSTORE_SYSTEM || !FeatureConfig.IsSupportDataTurboWhenNASIsSupported,
                    },
                    {
                        name: i18n.get('service.NFSClient_term_title'),
                        url: '/file/nfs-client',
                        show: [
                            {
                                permission: ['nfs_service_R'],
                            },
                        ],
                        hide: !FeatureConfig.IsSupportNFSClient,
                    },
                    {
                        name: i18n.get('common.accessUser_title'),
                        url: '/file/access-user',
                        show: [
                            {
                                permission: ['share_R'],
                            },
                        ],
                    },
                    {
                        name: i18n.get('common.quota_title'),
                        url: '/file/quota',
                        show: [
                            {
                                permission: ['quota_R'],
                            },
                        ],
                    },
                ],
            },
        ] : [
            {
                title: i18n.get('common.fileService_title'),
                children: [
                    {
                        name: i18n.get('common.EnumDefine_RUNNING_STATUS_E_UNACTIVATED'),
                        url: '/file/iniliziation',
                        show: [
                            {
                                license: [LicenseKey.NAS],
                            },
                        ],
                        hide: SWITCH.IS_VSTORE_SYSTEM,
                    },
                ],
            },
        ]),
        {
            title: i18n.get('system.network_tiitle'),
            children: [
                {
                    name: i18n.get('common.LogicalPorts'),
                    url: '/network/lif',
                    show: [
                        {
                            permission: ['port_R'],
                        },
                    ],
                },
                ...(SWITCH.IS_VSTORE_SYSTEM ? [] : [
                    {
                        name: i18n.get('system.network_eth_tiitle'),
                        url: '/network/eth',
                        show: [
                            {
                                permission: ['port_R'],
                            },
                        ],
                    },
                    {
                        name: i18n.get('system.network_roce_title'),
                        url: '/network/roce',
                        show: [
                            {
                                permission: ['port_R'],
                            },
                        ],
                    },
                ]),
                {
                    name: i18n.get('system.network_fc_tiitle'),
                    url: '/network/fc',
                    show: [
                        {
                            permission: ['port_R'],
                        },
                    ],
                    hide: SWITCH.IS_DIRECT_MODE,
                },
                {
                    name: i18n.get('system.network_netSpaces_title'),
                    url: '/network/net-space',
                    show: [
                        {
                            permission: ['netspace_R'],
                        },
                    ],
                    hide: SWITCH.IS_VSTORE_SYSTEM || !SWITCH.SUPPORT_NET_SPACE,
                },
                // 交换机端口
                {
                    name: i18n.get('system.hardware_term_SOSwitchPorts_tab'),
                    url: '/network/switch-port',
                    show: [
                        {
                            permission: ['port_R'],
                        },
                    ],
                    hide: SWITCH.IS_VSTORE_SYSTEM || !SWITCH.SUPPORT_SHOW_SWITCH,
                },
            ],
        },
        {
            title: i18n.get('common.smartMigration_title'),
            children: [
                {
                    name: i18n.get('storage.smartCache_term_title'),
                    url: '/tuning/smart-cache',
                    show: [
                        {
                            permission: ['smart_cache_R'],
                        },
                    ],
                    hide: SWITCH.IS_VSTORE_SYSTEM || !FeatureConfig.IsSupportSmartCache,
                },
                // 微存储直通模式不支持smartqos
                ...(SWITCH.IS_DIRECT_MODE ? [] : [
                    {
                        name: i18n.get('monitor.charts_term_objectType_SmartQoS'),
                        url: '/tuning/smart-qos',
                        show: [
                            {
                                permission: ['smart_qos_R'],
                                license: FeatureConfig.IsSupportSmartQosNoNeedLicense ? [] : [LicenseKey.SmartQoS],
                            },
                        ],
                    },
                ]),
                {
                    name: i18n.get('storage.smartMigration_menu'),
                    url: '/tuning/smart-migration',
                    show: [
                        {
                            permission: ['smart_migration_R'],
                            license: [LicenseKey.SmartMigration, LicenseKey.SmartThin],
                        },
                    ],
                    hide: !FeatureConfig.IsSupportSmartMigration,
                },
                {
                    name: i18n.get('storage.smartMove_menu'),
                    url: '/tuning/fs-smart-move',
                    show: [
                        {
                            permission: [['smart_move_R',
                                'inter_smart_move_domain_R', 'inter_smart_move_vstore_R', 'inter_smart_move_fs_R']],
                            license: [LicenseKey.SmartMove],
                        },
                    ],
                    hide: !SWITCH.SUPPORT_SMART_MOVE,
                },
                {
                    name: i18n.get('service.Cloudtier_task_title'),
                    url: '/tuning/smart-mobility',
                    hide: !ShardSwitch.CLOUD_TIER,
                    show: [
                        {
                            permission: ['smart_mobility_R'],
                            license: [[LicenseKey.NAS], [LicenseKey.SmartMobilityNAS, LicenseKey.SmartMobilityObject]],
                        },
                    ],
                },
            ],
        },
        {
            title: i18n.get('storage.containerStorage_task_title'),
            children: [
                {
                    name: i18n.get('storage.containerStorage_task_pod_title'),
                    url: '/container-storage/pod',
                    show: [
                        {
                            permission: ['container_storage_R'],
                        },
                    ],
                },
                {
                    name: i18n.get('storage.containerStorage_task_pv_title'),
                    url: '/container-storage/pv',
                    show: [
                        {
                            permission: ['container_storage_R'],
                        },
                    ],
                },
            ],
        },
    ];
    // 二级存储服务模块菜单
    const oceanProtectServiceMenus = [
        ...(SWITCH.SUPPORT_NAS ? [
            {
                title: i18n.get('common.fileService_title'),
                children: [
                    {
                        name: i18n.get('common.fileSystem_title'),
                        url: '/file/file-system',
                        show: [
                            {
                                permission: ['file_system_R'],
                            },
                        ],
                    },
                    {
                        name: i18n.get('common.dtree_lab'),
                        url: '/file/dtree',
                        show: [
                            {
                                permission: ['dtree_R'],
                            },
                        ],
                    },
                    {
                        name: i18n.get('common.share_title'),
                        url: '/file/share',
                        show: [
                            {
                                permission: ['share_R'],
                            },
                        ],
                    },
                    {
                        name: i18n.get('service.DataTurboClient_title'),
                        url: '/file/data-turbo-client',
                        show: [
                            {
                                permission: ['data_turbo_R'],
                            },
                        ],
                        hide: SWITCH.IS_VSTORE_SYSTEM,
                    },
                    {
                        name: i18n.get('service.NFSClient_term_title'),
                        url: '/file/nfs-client',
                        show: [
                            {
                                permission: ['nfs_service_R'],
                            },
                        ],
                        hide: !FeatureConfig.IsSupportNFSClient,
                    },
                    {
                        name: i18n.get('common.accessUser_title'),
                        url: '/file/access-user',
                        show: [
                            {
                                permission: ['share_R'],
                            },
                        ],
                    },
                    {
                        name: i18n.get('common.quota_title'),
                        url: '/file/quota',
                        show: [
                            {
                                permission: ['quota_R'],
                            },
                        ],
                    },
                ],
            },
        ] : [
            {
                title: i18n.get('common.fileService_title'),
                children: [
                    {
                        name: i18n.get('common.EnumDefine_RUNNING_STATUS_E_UNACTIVATED'),
                        url: '/file/iniliziation',
                        show: [
                            {
                                license: [LicenseKey.NAS],
                            },
                        ],
                        hide: SWITCH.IS_VSTORE_SYSTEM,
                    },
                ],
            },
        ]),
        {
            title: i18n.get('system.network_tiitle'),
            children: [
                {
                    name: i18n.get('common.LogicalPorts'),
                    url: '/network/lif',
                    show: [
                        {
                            permission: ['port_R'],
                        },
                    ],
                },
                ...(SWITCH.IS_VSTORE_SYSTEM ? [] : [
                    {
                        name: i18n.get('system.network_eth_tiitle'),
                        url: '/network/eth',
                        show: [
                            {
                                permission: ['port_R'],
                            },
                        ],
                    },
                    ...(!FeatureConfig.IsSupportNVMEOverRoCENetWorkAndLif ? [] : [
                        {
                            name: i18n.get('system.network_roce_title'),
                            url: '/network/roce',
                            show: [
                                {
                                    permission: ['port_R'],
                                },
                            ],
                        },
                    ]),
                ]),
                {
                    name: i18n.get('system.network_fc_tiitle'),
                    url: '/network/fc',
                    show: [
                        {
                            permission: ['port_R'],
                        },
                    ],
                },
                {
                    name: i18n.get('system.network_netSpaces_title'),
                    url: '/network/net-space',
                    show: [
                        {
                            permission: ['netspace_R'],
                        },
                    ],
                    hide: SWITCH.IS_VSTORE_SYSTEM || !SWITCH.SUPPORT_NET_SPACE,
                },
                // 交换机端口
                {
                    name: i18n.get('system.hardware_term_SOSwitchPorts_tab'),
                    url: '/network/switch-port',
                    show: [
                        {
                            permission: ['port_R'],
                        },
                    ],
                    hide: SWITCH.IS_VSTORE_SYSTEM || !SWITCH.SUPPORT_SHOW_SWITCH,
                },
            ],
        },
        ...(SWITCH.IS_VSTORE_SYSTEM ? [] : [
            {
                title: i18n.get('service.vstore_term_vstoreService_title'),
                children: [
                    {
                        name: i18n.get('service.vstore_term_vstores_title'),
                        url: '/v-store-service/v-store',
                        show: [
                            {
                                permission: ['@vstore_R'],
                            },
                        ],
                    },
                ],
            },
        ]),
        {
            title: i18n.get('common.blockService_title'),
            children: [
                {
                    name: i18n.get('common.lunGroup_title'),
                    url: '/block/lun-group',
                    show: [
                        {
                            permission: [['lun_R', 'recycle_bin_R', 'lun_group_R']],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                },
                {
                    name: i18n.get('common.hostGroups_title'),
                    url: '/block/hosts',
                    show: [
                        {
                            permission: ['host_R'],
                            license: [LicenseKey.SmartThin],
                        },
                        {
                            permission: ['host_group_R'],
                            license: [LicenseKey.SmartThin],
                        },
                        {
                            permission: ['initiator_R'],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                },
                {
                    name: i18n.get('common.portGroups_title'),
                    url: '/block/port-group',
                    show: [
                        {
                            permission: ['port_group_R'],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                },
                {
                    name: i18n.get('storage.vvol_task_vvol_tittle'),
                    url: '/block/vvol',
                    show: [
                        {
                            permission: [['lun_R', 'protect_group_R']],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                    hide: !FeatureConfig.IsSupportVVOL,
                },
                // 通道LUN
                {
                    name: i18n.get('storage.channelLUN_task_title'),
                    url: '/block/channel-lun',
                    hide: !FeatureConfig.IsSupportChannelLun,
                    show: [
                        {
                            permission: [['lun_R']],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                },
                {
                    name: i18n.get('storage.mappingView_task_title'),
                    url: '/block/mapping-views',
                    show: [
                        {
                            permission: ['mapping_view_R'],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                },
            ],
        },
        {
            title: i18n.get('common.smartMigration_title'),
            children: [
                ...(SWITCH.IS_VSTORE_SYSTEM ? [] : [{
                    name: i18n.get('storage.smartCache_term_title'),
                    url: '/tuning/smart-cache',
                    show: [
                        {
                            permission: ['smart_cache_R'],
                        },
                    ],
                    hide: SWITCH.IS_VSTORE_SYSTEM || !FeatureConfig.IsSupportSmartCache,
                }]),
                {
                    name: i18n.get('monitor.charts_term_objectType_SmartQoS'),
                    url: '/tuning/smart-qos',
                    show: [
                        {
                            permission: ['smart_qos_R'],
                            license: FeatureConfig.IsSupportSmartQosNoNeedLicense ? [] : [LicenseKey.SmartQoS],
                        },
                    ],
                },
                {
                    name: i18n.get('storage.smartMigration_menu'),
                    url: '/tuning/smart-migration',
                    show: [
                        {
                            permission: ['smart_migration_R'],
                            license: [LicenseKey.SmartMigration, LicenseKey.SmartThin],
                        },
                    ],
                    hide: !FeatureConfig.IsSupportSmartMigration,
                },
                {
                    name: i18n.get('storage.smartMove_menu'),
                    url: '/tuning/fs-smart-move',
                    show: [
                        {
                            permission: [['smart_move_R',
                                'inter_smart_move_domain_R', 'inter_smart_move_vstore_R', 'inter_smart_move_fs_R']],
                            license: [LicenseKey.SmartMove],
                        },
                    ],
                    hide: !SWITCH.SUPPORT_SMART_MOVE,
                },
                {
                    name: i18n.get('service.Cloudtier_task_title'),
                    url: '/tuning/smart-mobility',
                    hide: !ShardSwitch.CLOUD_TIER,
                    show: [
                        {
                            permission: ['smart_mobility_R'],
                            license: [[LicenseKey.NAS], [LicenseKey.SmartMobilityNAS, LicenseKey.SmartMobilityObject]],
                        },
                    ],
                },
            ],
        },
    ];
    // dorado数据保护菜单
    const generalProtectMenus = [
        {
            name: i18n.get('common.dataProtection_title'),
            url: '/protection',
            children: [
                {
                    title: i18n.get('protection.dataProtection_term_prtotectionEntities_label'),
                    children: [
                        ...(SWITCH.SUPPORT_NAS && !SWITCH.IS_VSTORE_SYSTEM ? [
                            {
                                name: i18n.get('service.vstore_term_vstores_title'),
                                url: '/v-store',
                                show: [
                                    {
                                        permission: ['hyper_metro_pair_R', '@vstore_R'],
                                    },
                                ],
                            },
                        ] : []),
                        {
                            name: i18n.get('protection.protectionGroup_title'),
                            url: '/object/protect-group',
                            show: [
                                {
                                    permission: ['protect_group_R'],
                                    license: [LicenseKey.SmartThin],
                                },
                            ],
                        },
                        {
                            name: i18n.get('common.lun_title'),
                            url: '/object/lun',
                            show: [
                                {
                                    permission: [['lun_R'], ['lun_snapshot_R', 'hyper_clone_R', 'remote_replication_R',
                                        'hyper_metro_pair_R', 'dr_star_R']],
                                    license: [LicenseKey.SmartThin],
                                },
                            ],
                        },
                        ...(SWITCH.SUPPORT_NAS ? [
                            {
                                name: i18n.get('common.fileSystem_title'),
                                url: '/object/filesystem',
                                show: [
                                    [
                                        {
                                            permission: ['file_system_R'],
                                        },
                                    ], [
                                        {
                                            permission: ['fs_snapshot_R'],
                                            license: [['HyperSnap', 'HyperClone', 'HyperCDP']],
                                        },
                                        {
                                            permission: ['remote_replication_R'],
                                            license: ['HyperReplication'],
                                        },
                                        {
                                            license: ['HyperClone'],
                                        },
                                        {
                                            permission: ['hyper_metro_pair_R'],
                                        },
                                    ],
                                ],
                            },
                        ] : []),
                    ],
                },
                {
                    title: i18n.get('common.plan_title'),
                    children: [
                        {
                            name: i18n.get('protection.HyperCDP_term_block_title'),
                            url: '/plan/hyper-cdp',
                            show: [
                                {
                                    permission: [['lun_R', 'protect_group_R'], ['@hyper_cdp_R']],
                                    license: [LicenseKey.HyperCDP, LicenseKey.SmartThin],
                                },
                            ],
                        },
                        ...(SWITCH.SUPPORT_NAS ? [
                            {
                                name: i18n.get('protection.HyperCDP_term_fs_title'),
                                url: '/plan/file-hyper-cdp',
                                show: [
                                    {
                                        permission: ['file_system_R', '@hyper_cdp_R'],
                                    },
                                ],
                            },
                        ] : []),
                    ],
                },
                ...(SWITCH.IS_VSTORE_SYSTEM ? [] : [{
                    title: i18n.get('common.configure_title'),
                    children: [
                        {
                            name: i18n.get('common.remoteDevices_title'),
                            url: '/config/remote-device',
                            show: [
                                {
                                    permission: ['remote_device_R'],
                                },
                            ],
                        },
                        {
                            name: i18n.get('common.HyperMetroDomains_title'),
                            url: '/config/hyper-metro-domain',
                            show: [
                                {
                                    permission: ['hyper_metro_domain_R'],
                                    license: [[LicenseKey.NAS, LicenseKey.HyperMetro]],
                                },
                            ],
                        },
                        {
                            name: i18n.get('common.cloudBackup_title'),
                            externalLink: `https://${window.location.hostname}:25080/#/login`,
                            hide: authService.isRemoteAssistanceUser(),
                            show: [
                                {
                                    permission: ['container_application_R'],
                                    license: [LicenseKey.CloudBackup],
                                },
                            ],
                        },
                        {
                            name: i18n.get('common.HyperDetect_title'),
                            hide: authService.isRemoteAssistanceUser() ||
                                !localStorageService.get(STORAGE_KEY.ANTIRANSOMWARE_APPLICATION_CHECK),
                            externalLink: `https://${window.location.hostname}:25080/#/login`,
                            show: [
                                {
                                    permission: ['container_application_R'],
                                    license: [[LicenseKey.AntiRansomware, LicenseKey.ANTIRANSOMWARE_FOR_SAN]],
                                },
                            ],
                        },
                        {
                            name: i18n.get('common.HyperDetect_title'),
                            hide: authService.isRemoteAssistanceUser() ||
                                localStorageService.get(STORAGE_KEY.ANTIRANSOMWARE_APPLICATION_CHECK),
                            url: '/config/deploy-antiransomware',
                            show: [
                                {
                                    permission: ['container_application_R'],
                                    license: [[LicenseKey.AntiRansomware, LicenseKey.ANTIRANSOMWARE_FOR_SAN]],
                                },
                            ],
                        },
                    ],
                }]),
            ],
        },
    ];
    // 二级存储数据保护菜单
    const oceanProtectMenus = [
        {
            name: i18n.get('common.dataProtection_title'),
            url: '/protection',
            children: [
                {
                    children: [
                        {
                            name: i18n.get('common.snapshot_title'),
                            url: '/snapshot',
                            show: [
                                {
                                    permission: [['fs_snapshot_R', 'lun_snapshot_R', 'snap_group_R']],
                                    license: [['HyperSnap', 'HyperClone', 'HyperCDP']],
                                },
                            ],
                        },
                        {
                            name: i18n.get('protection.fs_hyperClone_task_clones_title'),
                            url: '/clone',
                            show: [
                                {
                                    permission: [['hyper_clone_group_R', 'hyper_clone_R', 'file_system_R']],
                                    license: ['HyperClone'],
                                },
                            ],
                        },
                        {
                            name: i18n.get('common.replication_title'),
                            url: '/replication',
                            show: [
                                {
                                    permission: [['remote_replication_R', 'consistency_group_R']],
                                    license: ['HyperReplication'],
                                },
                            ],
                        },
                        {
                            name: i18n.get('protection.protectionGroup_title'),
                            url: '/protection-group',
                            show: [
                                {
                                    permission: ['protect_group_R'],
                                    license: [LicenseKey.SmartThin],
                                },
                            ],
                        },
                        {
                            name: i18n.get('protection.CDP_term_fs_title'),
                            url: '/plan/file-hyper-cdp',
                            show: [
                                {
                                    permission: ['file_system_R', '@hyper_cdp_R'],
                                },
                            ],
                        },
                        {
                            name: i18n.get('protection.CDP_term_block_title'),
                            url: '/plan/hyper-cdp',
                            show: [
                                {
                                    permission: [['lun_R', 'protect_group_R'], ['@hyper_cdp_R']],
                                    license: [LicenseKey.HyperCDP, LicenseKey.SmartThin],
                                },
                            ],
                        },
                        ...(SWITCH.IS_VSTORE_SYSTEM ? [] : [
                            {
                                name: i18n.get('common.remoteDevices_title'),
                                url: '/config/remote-device',
                                show: [
                                    {
                                        permission: ['remote_device_R'],
                                    },
                                ],
                            },
                            {
                                name: i18n.get('common.HyperDetect_title'),
                                hide: authService.isRemoteAssistanceUser() ||
                                    !localStorageService.get(STORAGE_KEY.ANTIRANSOMWARE_APPLICATION_CHECK),
                                externalLink: `https://${window.location.hostname}:25080/#/login`,
                                show: [
                                    {
                                        permission: ['container_application_R'],
                                        license: [[LicenseKey.AntiRansomware, LicenseKey.ANTIRANSOMWARE_FOR_SAN]],
                                    },
                                ],
                            },
                            {
                                name: i18n.get('common.HyperDetect_title'),
                                hide: authService.isRemoteAssistanceUser() ||
                                    localStorageService.get(STORAGE_KEY.ANTIRANSOMWARE_APPLICATION_CHECK),
                                url: '/config/deploy-antiransomware',
                                show: [
                                    {
                                        permission: ['container_application_R'],
                                        license: [[LicenseKey.AntiRansomware, LicenseKey.ANTIRANSOMWARE_FOR_SAN]],
                                    },
                                ],
                            },
                        ]),
                    ],
                },
            ],
        },
    ];

    return [
        {
            ...(SWITCH.IS_VSTORE_SYSTEM ? {
                name: i18n.get('common.mainPage_title'),
                url: '/vstore/home',
            } : {
                name: i18n.get('common.mainPage_title'),
                url: '/home',
            }),
        },
        {
            name: i18n.get('common.service_title'),
            url: '/service',
            children: !FeatureConfig.IsShowMenuItemsForOceanProtect ? generalServiceMenus : oceanProtectServiceMenus,
        },
        ...(!FeatureConfig.IsShowMenuItemsForOceanProtect ? generalProtectMenus : oceanProtectMenus),
        {
            name: i18n.get('common.monitor_title'),
            url: '/monitor',
            children: [
                {
                    children: [
                        ...(SWITCH.IS_VSTORE_SYSTEM ? [] : [
                            {
                                name: i18n.get('common.alarmAndEvent_title'),
                                url: '/alarms-and-events',
                                show: [
                                    {
                                        permission: ['alarm_R'],
                                    },
                                ],
                            },
                            {
                                name: i18n.get('monitor.diagnosis_task_title'),
                                url: '/fault-tree-diagnosis',
                                show: [
                                    {
                                        permission: ['fault_diagnose_R'],
                                    },
                                ],
                                hide: !SWITCH.SUPPORT_FAULT_TREE_DIAGNOSIS,
                            },
                            {
                                name: i18n.get('common.performance_title'),
                                url: '/performance',
                                show: [
                                    {
                                        permission: FeatureConfig.IsSupportQueryPowerSupply ? [['performance_R']] :
                                            [['performance_R', 'power_supply_R']],
                                    },
                                ],
                            },
                            // 2910和微存储屏蔽预测
                            ...(!(SWITCH.SUPPORT_CAPACITY_PREDICT) ? [] : [
                                {
                                    name: i18n.get('common.forecasting_newtitle'),
                                    url: '/forecasting',
                                    show: [
                                        {
                                            permission: [['performance_R']],
                                        },
                                    ],
                                },
                            ]),
                            {
                                name: i18n.get('monitor.dataExport_title'),
                                url: '/schedule-report',
                                show: [
                                    {
                                        permission: ['performance_R'],
                                    },
                                ],
                                hide: FeatureConfig.IsHidePms || authService.isRemoteAssistanceUser(),
                            },
                        ]),
                        {
                            name: i18n.get('common.taskCenter_title'),
                            url: '/task',
                            show: [
                                {
                                    permission: ['task_R'],
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        ...(SWITCH.IS_VSTORE_SYSTEM ? [] : [
            {
                name: i18n.get('common.system_title'),
                url: '/system',
                children: [
                    {
                        children: [
                            {
                                name: i18n.get('common.hardware_title'),
                                url: '/hardware',
                                show: [
                                    {
                                        permission: [
                                            // 微存储不支持机柜&级联版【不需要该权限的依赖】
                                            ...(FeatureConfig.IsHideCabinetAndExpansion ? [] : [
                                                'cabinet_R',
                                                'expansion_module_R',
                                            ]),
                                            'enclosure_R',
                                            'controller_R',
                                            'interface_module_R',
                                            'fan_R',
                                            'power_supply_R',
                                            'bbu_R',
                                            'disk_R',
                                            'port_R',
                                        ],
                                    },
                                ],
                            },
                            {
                                name: i18n.get('common.externalStorage_title'),
                                url: '/external-storage',
                                show: [
                                    {
                                        permission: ['remote_device_R'],
                                        license: [LicenseKey.SmartThin],
                                    },
                                ],
                            },
                            // 微存储直通模式屏蔽存储池
                            ...(SWITCH.IS_DIRECT_MODE ? [] : [
                                {
                                    name: i18n.get('common.storagePool_title'),
                                    url: '/storage-pool',
                                    show: [
                                        {
                                            permission: ['storage_pool_R', 'disk_domain_R'],
                                        },
                                    ],
                                },
                            ]),
                            {
                                name: i18n.get('system.container_task_title'),
                                url: '/smart-container',
                                show: [
                                    {
                                        permission: [
                                            FeatureConfig.IsSupportVM ? ['vm_service_R'] : ['container_service_R'],
                                            ['task_R'],
                                            ['controller_R'],
                                            ['lun_R'],
                                            ['upgrade_R'],
                                        ],
                                        license: [[
                                            LicenseKey.SmartContainerLicense,
                                            LicenseKey.SmartContainerLicenseStandard,
                                            LicenseKey.SmartContainerLicensePremium,
                                            LicenseKey.CloudBackup,
                                            LicenseKey.DataBackUpCapacity,
                                            LicenseKey.AntiRansomware,
                                            LicenseKey.ANTIRANSOMWARE_FOR_SAN,
                                            LicenseKey.S3,
                                            LicenseKey.VirtualEnvironmentEnabler,
                                        ]],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ]),
        {
            name: i18n.get('common.setting_title'),
            url: '/settings',
            children: [
                {
                    children: [
                        ...(SWITCH.IS_VSTORE_SYSTEM ? [] : [
                            {
                                name: i18n.get('common.basicInfo_title'),
                                url: '/basic-infomation',
                            },
                            {
                                name: i18n.get('common.blockStorageService_title'),
                                url: '/block-storage-service',
                                show: [
                                    {
                                        permission: [['target_R', 'initiator_R', 'isns_R', 'host_R']],
                                        license: [LicenseKey.SmartThin],
                                    },
                                ],
                                hide: SWITCH.IS_DIRECT_MODE,
                            },
                        ]),
                        ...(SWITCH.SUPPORT_NAS ? [
                            {
                                name: i18n.get('settings.fileStorageService_term_tittle'),
                                url: '/file-storage-service',
                                show: [
                                    {
                                        permission: [SWITCH.IS_VSTORE_SYSTEM ?
                                            ['domain_R', 'dns_zone_R', 'ldap_R', 'kerberos_R',
                                                'nfs_service_R', 'cifs_service_R', '@ndmp_service_R', '@audit_strategy_R',
                                                ...(!ProductConfig.isSupportAntivirus ? [] : ['antivirus_R']),
                                            ] :
                                            ['nfs_service_R', 'cifs_service_R',
                                                '@ndmp_service_R', '@audit_strategy_R',
                                                ...(!ProductConfig.isSupportAntivirus ? [] : ['antivirus_R']),
                                            ]],
                                    },
                                ],
                            },
                        ] : [
                            {
                                name: i18n.get('settings.fileStorageService_term_tittle'),
                                url: '/iniliziation',
                                show: [
                                    {
                                        license: [LicenseKey.NAS],
                                    },
                                ],
                                hide: SWITCH.IS_VSTORE_SYSTEM,
                            },
                        ]),
                        {
                            name: i18n.get('settings.certificateManage_clientCertificates_col'),
                            url: '/certificate-management',
                            show: [
                                {
                                    permission: ['certificate_R'],
                                },
                            ],
                        },
                        {
                            name: i18n.get('common.userAndSecurity_title'),
                            url: '/users-security/user-role',
                        },
                        ...(SWITCH.IS_VSTORE_SYSTEM ? [] : [
                            // 密钥服务
                            {
                                name: i18n.get('common.keyService_title'),
                                url: '/key-service',
                                devide: true,
                                show: [
                                    {
                                        permission: ['kmc_R'],
                                    },
                                ],
                            },
                            // 微存储直通场景屏蔽license
                            ...(SWITCH.IS_DIRECT_MODE ? [] : [
                                {
                                    name: i18n.get('common.manageLicense_title'),
                                    url: '/license-management',
                                },
                            ]),
                            {
                                name: i18n.get('common.setAlarm_title'),
                                url: '/alarm/alarm-notify',
                                devide: true,
                                show: [
                                    {
                                        permission: ['alarm_R'],
                                    },
                                ],
                            },
                            {
                                name: i18n.get('common.snmpManagement_title'),
                                url: '/snmp/snmp-protocol',
                                show: [
                                    {
                                        permission: ['snmp_R'],
                                    },
                                ],
                            },
                            {
                                name: i18n.get('common.monitorSetting_title'),
                                url: '/monitor',
                                show: [
                                    {
                                        permission: ['performance_R'],
                                    },
                                ],
                            },
                            {
                                name: i18n.get('settings.eService_title'),
                                url: '/e-service',
                                show: [
                                    {
                                        permission: ['call_home_R'],
                                    },
                                ],
                                hide: !FeatureConfig.IsSupportEService,
                            },
                            {
                                name: i18n.get('settings.container_task_setting_title'),
                                url: '/container-configuration',
                                show: [
                                    {
                                        permission: FeatureConfig.IsSupportVM ? ['vm_service_R'] : ['container_service_R'],
                                        license: [[
                                            LicenseKey.SmartContainerLicense,
                                            LicenseKey.SmartContainerLicenseStandard,
                                            LicenseKey.SmartContainerLicensePremium,
                                            LicenseKey.CloudBackup,
                                            LicenseKey.DataBackUpCapacity,
                                            LicenseKey.AntiRansomware,
                                            LicenseKey.ANTIRANSOMWARE_FOR_SAN,
                                            LicenseKey.S3,
                                            LicenseKey.VirtualEnvironmentEnabler,
                                        ]],
                                    },
                                ],
                            },
                            // {
                            //     name: i18n.get('common.preference_title'),
                            //     url: '/preferences'
                            // }
                        ]),
                    ],
                },
            ],
        },
    ];
}
